import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';

const navigation = [
  { to: '/dashboard', labelKey: 'nav.dashboard', descriptionKey: 'navDescriptions.dashboard' },
  { to: '/patients', labelKey: 'nav.patients', descriptionKey: 'navDescriptions.patients' },
  { to: '/appointments', labelKey: 'nav.appointments', descriptionKey: 'navDescriptions.appointments' },
  { to: '/medical-records', labelKey: 'nav.medicalRecords', descriptionKey: 'navDescriptions.medicalRecords' },
  { to: '/invoices', labelKey: 'nav.invoices', descriptionKey: 'navDescriptions.invoices' },
  { to: '/inventory', labelKey: 'nav.inventory', descriptionKey: 'navDescriptions.inventory' },
  { to: '/users', labelKey: 'nav.users', descriptionKey: 'navDescriptions.users' },
];

const MainLayout: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);

      // Auto-open sidebar on desktop, close on mobile
      if (desktop && !isSidebarOpen) {
        setIsSidebarOpen(true);
      } else if (!desktop && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  // Get active nav item
  const activeNavItem = useMemo(() => {
    if (location.pathname === '/' || location.pathname === '') {
      return navigation[0];
    }
    return navigation.find((item) => location.pathname.startsWith(item.to));
  }, [location.pathname]);

  const activeNavLabel = activeNavItem ? t(activeNavItem.labelKey) : t('nav.dashboard');
  const activeNavDescription = activeNavItem ? t(activeNavItem.descriptionKey) : '';

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} isDesktop={isDesktop} onClose={handleCloseSidebar} />

      {/* Main Content */}
      <div className="flex flex-1 flex-col">
        {/* Navbar */}
        <Navbar
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={handleToggleSidebar}
          activeNavLabel={activeNavLabel}
          activeNavDescription={activeNavDescription}
        />

        {/* Page Content */}
        <main className="relative flex-1 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 lg:p-8">
          {/* Decorative background */}
          <div className="pointer-events-none absolute inset-0 opacity-30">
            <div className="absolute -top-16 right-6 h-64 w-64 rounded-full bg-indigo-400/20 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-violet-400/20 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-white py-6 text-center text-sm text-slate-500">
          <div className="mx-auto max-w-7xl px-4">
            <p>
              &copy; {new Date().getFullYear()} {t('common.appName')}. {t('layout.rights')}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MainLayout;
